import { type ReactNode, useState } from "react";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

type Props = {
  children: ReactNode;
  value?: string;
};

export const CopyableText = ({ children, value }: Props) => {
  const [copied, setCopied] = useState(false);

  const textToCopy =
    value ?? (typeof children === "string" ? children : String(children));

  const handleCopy = () => {
    void navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Tooltip
      title={copied ? "Copied!" : "Click to copy"}
      arrow
      placement="right"
    >
      <Box
        onClick={handleCopy}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: 0.75,
          py: 0.25,
          mx: -0.75,
          cursor: "pointer",
          borderRadius: 1,
          transition: "background-color 0.15s",
          "&:hover": { backgroundColor: "action.hover" },
          "& .copy-icon": { opacity: 0, transition: "opacity 0.15s" },
          "&:hover .copy-icon": { opacity: 1 },
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {children}
        </Typography>
        {copied ? (
          <CheckIcon sx={{ fontSize: 14, color: "success.main" }} />
        ) : (
          <ContentCopyIcon
            className="copy-icon"
            sx={{ fontSize: 14, color: "text.disabled" }}
          />
        )}
      </Box>
    </Tooltip>
  );
};
